import { defineStore } from 'pinia';
import http from '@/services/http';
import cacheService from '@/services/cacheService';

// Pinia-Store für Test-Einträge
export const useEntriesStore = defineStore('entries', {
  state: () => ({
    entries: [],
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0
    },
    filters: {
      search: '',
      test_id: '',
      sort_by: 'created_at'
    },
    loading: false,
    error: null,
    currentEntry: null
  }),
  
  getters: {
    entriesList: (state) => Array.isArray(state.entries) ? state.entries : [],
    paginationInfo: (state) => state.pagination,
    filtersActive: (state) => !!state.filters.search || !!state.filters.test_id,
    getCurrentEntry: (state) => state.currentEntry
  },
  
  actions: {
    setLoading(status) {
      this.loading = status;
    },
    
    setError(error) {
      this.error = error;
    },
    
    async fetchEntries() {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log('Entries werden abgerufen mit Filtern:', this.filters);
        
        // Hier würde normaleweise ein API-Aufruf stehen
        const response = await http.get('/api/entries', { params: this.filters });
        
        // Prüfen, ob die Antwort das erwartete Format hat
        if (response.data && response.data.data) {
          this.entries = response.data.data;
          
          // Sicherstellen, dass alle Einträge gültig sind
          this.entries = this.entries.filter(entry => entry && entry.id);
          
          // Pagination-Infos extrahieren, falls vorhanden
          if (response.data.current_page !== undefined) {
            this.pagination = {
              current_page: response.data.current_page,
              last_page: response.data.last_page || 1,
              per_page: response.data.per_page || 10,
              total: response.data.total || 0
            };
          }
        } else {
          // Falls die Antwort nicht das erwartete Format hat
          console.warn('API-Antwort hat nicht das erwartete Format:', response);
          this.entries = Array.isArray(response.data) ? response.data : [];
        }
        
        return this.entries;
      } catch (error) {
        console.error('Fehler beim Abrufen der Einträge:', error);
        this.setError('Fehler beim Laden der Einträge');
        return [];
      } finally {
        this.setLoading(false);
      }
    },
    
    async deleteEntry(id) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log(`Eintrag ${id} wird gelöscht...`);
        // Hier würde normaleweise ein API-Aufruf stehen
        // Beispiel: await http.delete(`/api/entries/${id}`);
        
        // Lokale Entfernung
        this.entries = this.entries.filter(entry => entry.id !== id);
        
        return true;
      } catch (error) {
        console.error(`Fehler beim Löschen des Eintrags ${id}:`, error);
        this.setError('Fehler beim Löschen des Eintrags');
        return false;
      } finally {
        this.setLoading(false);
      }
    },
    
    setFilters(filters) {
      this.filters = { ...this.filters, ...filters };
      this.fetchEntries();
    },
    
    resetFilters() {
      this.filters = {
        search: '',
        test_id: '',
        sort_by: 'created_at'
      };
      this.pagination.current_page = 1;
      this.fetchEntries();
    },
    
    setPage(page) {
      this.pagination.current_page = page;
      this.fetchEntries();
    },
    
    async getAllEntries() {
      this.setLoading(true);
      this.setError(null);
      
      try {
        // Alle Einträge ohne Pagination abrufen
        const response = await http.get('/api/entries/all');
        
        if (response.data && response.data.data) {
          return response.data.data;
        } else {
          return Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error('Fehler beim Abrufen aller Einträge:', error);
        this.setError('Fehler beim Laden aller Einträge');
        return [];
      } finally {
        this.setLoading(false);
      }
    },
    
    async fetchEntry(id) {
      this.loading = true;
      this.error = null;
      
      try {
        console.log(`Entry ${id} wird abgerufen...`);
        const response = await http.get(`/api/entries/${id}`);
        this.currentEntry = response.data;
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Abrufen des Eintrags ${id}:`, error);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async addComment(payload) {
      this.loading = true;
      this.error = null;
      
      try {
        console.log(`Kommentar wird hinzugefügt zu Eintrag ${payload.entry_id}...`);
        const response = await http.post(`/api/entries/${payload.entry_id}/comments`, { content: payload.content });
        
        // Aktualisiere den aktuellen Eintrag, falls er geladen ist
        if (this.currentEntry && this.currentEntry.id === parseInt(payload.entry_id)) {
          if (!this.currentEntry.comments) {
            this.currentEntry.comments = [];
          }
          this.currentEntry.comments.push(response.data);
        }
        
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Hinzufügen des Kommentars:`, error);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async deleteComment(payload) {
      this.loading = true;
      this.error = null;
      
      try {
        console.log(`Kommentar ${payload.comment_id} wird gelöscht...`);
        await http.delete(`/api/entries/${payload.entry_id}/comments/${payload.comment_id}`);
        
        // Aktualisiere den aktuellen Eintrag, falls er geladen ist
        if (this.currentEntry && this.currentEntry.id === parseInt(payload.entry_id) && this.currentEntry.comments) {
          this.currentEntry.comments = this.currentEntry.comments.filter(
            comment => comment.id !== payload.comment_id
          );
        }
        
        return true;
      } catch (error) {
        console.error(`Fehler beim Löschen des Kommentars:`, error);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async sendEmail(payload) {
      this.loading = true;
      this.error = null;
      
      try {
        console.log(`E-Mail wird gesendet an Eintrag ${payload.entry_id}...`);
        const response = await http.post(`/api/entries/${payload.entry_id}/send-email`, payload);
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Senden der E-Mail:`, error);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async submitTestEntry(payload) {
      this.loading = true;
      this.error = null;
      
      try {
        console.log(`Test wird abgeschickt für Hash ${payload.testHash}...`);
        const response = await http.post(`/api/test/${payload.testHash}/create`, {
          ...payload.userData,
          answers: payload.answers
        });
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Absenden des Tests:`, error);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    }
  }
}); 